import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  MasterMapOptions,
  PickMainState,
} from "react-lib/apps/HISV3/common/CommonInterface";

import { ProjectListType } from "./ProjectList";

import { formatDateToYYYYMMDD, formatISOtoBEDateTime } from "react-lib/utils/dateUtils";

import { downloadXLSX } from "react-lib/apps/HISV3/common/CommonInterface";
import { utils as XLSXUtils, write as XLSXWrite } from "xlsx";

/*================================ APIs ==============================*/
// import GetRedmineToken from "issara-sdk/apis/GetRedmineToken_usersM";
import GetCustomFields from "issara-sdk/apis/GetCustomFields_usersM";

import {
  getIssuesList,
  getIssuesStatusList,
  getMembershipsList,
  getPriorityList,
  getProjectList,
  getTrackerList,
  createIssue,
  CreateIssueType,
  CreateIssuePayloadType,
  editIssue,
  getIssuesDetail,
} from "./RedmineAPIController";

/*================================ Type-Defining Constants ==============================*/

export const ACTIONS = {
  SEARCH: "SEARCH",
  CREATE: "CREATE",
  EDIT: "EDIT",
  HISTORY: "HISTORY",
  MEMBERSHIP: "MEMBERSHIP",
  EXPORT_EXCEL: "EXPORT_EXCEL",
} as const;

export const CARD_NAME = "IssueList" as const;

/*================================ Types and Interface ==============================*/

export type State = Partial<{
  IssueListSequence: Partial<{
    sequenceIndex: "Start" | "Action" | null;
    // common
    currentDivision: number;
    divisionHasUser: OptionsType[];
    customFields: CustomFieldsItemType[];
    customFieldsDivision: CustomFieldsListType[];
    trackerList: TrackerListType[];
    statusList: StatusListType[];
    priorityList: PriorityListType[];
    projectList: ProjectListType[];
    membershipList: { id: number; name: string }[];
    membershipProjectList: { id: number; name: string }[];
    // issue
    historyList: JournalsListType[];
    pagination: PaginatedItemsType;
  }> | null;
}>;

export type PickedState = PickMainState<
  "buttonLoadCheck" | "errorMessage" | "successMessage" | "masterOptions"
>;

export type MasterOptions = MasterMapOptions<typeof Masters>;

type PaginatedItemsType = Partial<{
  items: IssueListType[];
  activePage: number;
  total: number;
}>;

export type IssueListType = Partial<{
  id: number;
  project: ProjectItemListType;
  tracker: TrackerListType;
  status: StatusListType;
  priority: PriorityListType;
  author: AuthorType;
  assigned_to: AssignedToType;
  subject: string;
  description: any;
  start_date: any;
  due_date: any;
  done_ratio: number;
  is_private: boolean;
  estimated_hours: any;
  total_estimated_hours: any;
  custom_fields: CustomFieldsType[];
  created_on: string;
  updated_on: string;
  closed_on: any;
  note: string;
  journals: JournalsListType[];
}>;

export type JournalsListType = Partial<{
  id: number;
  user: AuthorType;
  notes: string;
  created_on: string;
  private_notes: boolean;
  details: JournalDetailType[];
}>;

export type JournalDetailType = {
  property: string;
  name: string;
  old_value: string;
  new_value: string;
};

export type FilterType = Partial<{
  project_id: number;
  start_date: string;
  end_date: string;
  subject: string;
  status_id: number | string;
  author_id: number;
  assigned_to_id: number;
  offset: number;
  limit: number;
  cf_1: string; // -- Division of Project
  cf_6: string | number; // -- Assigned Division of Issue
  // --- multiple
  projects: number[];
}>;

export type CustomFieldsItemType = {
  id: number;
  name: string;
  multiple: boolean;
  customized_type: "issue" | "project";
  possible_values?: CustomFieldsListType[];
};

export type CustomFieldsType = {
  id: number;
  name: string;
  value: string;
};

export type CustomFieldsListType = {
  label: string;
  value: string;
};

export type ProjectItemListType = {
  id: number;
  name: string;
};

export type TrackerListType = {
  id: number;
  name: string;
};

export type StatusListType = {
  id: number;
  name: string;
  is_closed: boolean;
};

export type PriorityListType = {
  id: number;
  name: string;
  is_default: boolean;
};

export type AuthorType = {
  id: number;
  name: string;
};

export type AssignedToType = {
  id: number;
  name: string;
};

export type MembershipItemType = {
  id: number;
  project: { id: number; name: string };
  user: { id: number; name: string };
  roles: { id: number; name: string }[];
};

export type MembershipMapType = Record<number, MembershipItemType[]>;

export type DivisionHasUserType = {
  division: DivisionListType;
  extra: string;
  role: number | null;
  triage_level: string;
  user: number;
};

type DivisionListType = Partial<{
  id: number;
  code: string;
  name: string;
  name_code: string;
  name_en: string;
  organization: number | null;
  profile: number | null;
  type: number;
  type_label: string;
}>;

type OptionsType = Partial<{
  key: number;
  value: number;
  text: string;
}>;

export type Event =
  | { message: "GetMasterData"; params: Record<string, unknown> }
  | { message: "RunSequence"; params: Record<string, unknown> };

export type Data = {
  device?: number;
  division?: number;
  divisionHasUser?: DivisionHasUserType[];
};

type SequenceName = typeof CARD_NAME;

export type SeqState = BaseSeqState<SequenceName>;

export type Actions = typeof ACTIONS;

type ActionPayloadMap = {
  [ACTIONS.SEARCH]: {
    filter?: FilterType;
    activePage?: number;
    // currentDivision: string;
    projectIdList: number[];
  };
  [ACTIONS.CREATE]: {
    createdData: CreateIssueType;
    // currentDivision: string;
    projectIdList: number[];
  };
  [ACTIONS.EDIT]: {
    issueId: number;
    editedData: CreateIssueType;
    activePage?: number;
    // currentDivision: string
    projectIdList: number[];
  };
  [ACTIONS.HISTORY]: { issueId: number };
  [ACTIONS.MEMBERSHIP]: { projectId: number };
  [ACTIONS.EXPORT_EXCEL]: { data: any }; // *
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = any, R = any> = ActionHandler<PickedState & State, Event, Data, P, R>;

/*================================ Constants ==============================*/

export const StateInitial: State = {
  IssueListSequence: null,
};

export const DataInitial: Data = {};

const Masters = [["division", {}]] as const; //  todo --- add users

export const BTN_ACTS = generatePrefixedActions(ACTIONS, CARD_NAME);

export const DEFAULT_LIMIT = 25;

/*================================ Functions ==============================*/

export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.handleEvent({
    message: "GetMasterData",
    params: {
      masters: Masters,
    },
  });

  const { customFieldsList, priorityList, statusList, trackerList } = await GetCommonList(
    controller,
    params
  );

  const [projResp, projErr] = await GetProjectList(controller, params);
  const projectList: ProjectListType[] = projResp?.projects || [];

  const membershipMap = await GetProjectMemberships(controller, { projectList });
  const uniqueUserList = extractUniqueUsers(membershipMap);
  const customFields = customFieldsList?.[0] ? customFieldsList[0].custom_fields : [];

  const divisionList = getSpecificCustomFieldList(customFields, "Division", "issue");

  const divisionHasUser = formatDivisionHasUserOptions(controller.data.divisionHasUser || []);
  const filteredProjectOptions = projectList
    .filter((project) => {
      const divisionField = project.custom_fields?.find((field) => field.name === "Division");
      const activeField = project.custom_fields?.find((field) => field.name === "Active");
      const isActive = activeField?.value === "1";
      const allowedDivisions = divisionHasUser.map((d) => d.text);

      if (!divisionField || !Array.isArray(divisionField.value)) return false;

      return (
        isActive &&
        divisionField.value.some((divName: string) => allowedDivisions.includes(divName))
      );
    })
    .map((project) => ({
      key: project?.id,
      value: project?.id || 0,
      text: project?.name,
    }));

  const projectIdMap = filteredProjectOptions.map((opt) => opt.value);

  const projectIdList = projectIdMap.length > 0 ? projectIdMap : [0];

  controller.setState(
    {
      IssueListSequence: {
        ...state.IssueListSequence,
        sequenceIndex: "Action",
        currentDivision: controller.data.division,
        divisionHasUser: divisionHasUser || [],
        customFields: customFields,
        customFieldsDivision: divisionList || [],
        priorityList: priorityList[0] ? priorityList[0].issue_priorities : [],
        statusList: statusList[0] ? statusList[0].issue_statuses : [],
        trackerList: trackerList[0] ? trackerList[0].trackers : [],
        projectList,
        membershipList: uniqueUserList || [],
      },
    },
    () => Action(controller, { ...params, action: ACTIONS.SEARCH, projectIdList })
  );
};

/*================================ Action ==============================*/

export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.SEARCH]: HandleSearch,
    [ACTIONS.MEMBERSHIP]: HandleGetMembership,
    [ACTIONS.CREATE]: HandleCreate,
    [ACTIONS.EDIT]: HandleEdit,
    [ACTIONS.HISTORY]: HandleHistory,
    [ACTIONS.EXPORT_EXCEL]: HandleExportExcel,
  };

  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    handler(controller, params);
  } else {
    console.error(`No handler found for action: ${action}`);
  }
};

/*================================ Handlers ==============================*/

const HandleSearch: Handler<Params<Actions["SEARCH"]>> = async (controller, params) => {
  const state = controller.getState();

  const { filter, projectIdList } = params;

  const activePage = params.activePage || 1;
  const offset = (activePage - 1) * DEFAULT_LIMIT;

  const filterAdded = {
    ...filter,
    projects: projectIdList,
    offset,
  };

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.SEARCH]: BLC_STATUS.LOADING,
    },
  });

  const [resp, err] = await GetIssueList(controller, { filter: filterAdded });

  if (err) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SEARCH]: BLC_STATUS.ERROR,
      },
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: err,
      },
    });

    return;
  }

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.SEARCH]: BLC_STATUS.SUCCESS,
    },
    IssueListSequence: {
      ...state.IssueListSequence,
      pagination: {
        activePage,
        items: resp.issues || [],
        total: resp.total_count || 0,
      },
    },
  });
};

const HandleCreate: Handler<Params<Actions["CREATE"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.CREATE]: BLC_STATUS.LOADING,
    },
  });

  const { createdData } = params;

  const masterCustomFields = state.IssueListSequence?.customFields || [];

  const data: CreateIssuePayloadType = {
    issue: {
      ...createdData,
      start_date: formatDateToYYYYMMDD(createdData.start_date),
      due_date: formatDateToYYYYMMDD(createdData.due_date),
      custom_fields: convertCustomFieldNameToId(
        createdData.custom_fields || [],
        masterCustomFields
      ),
    },
  };

  const [resp, err] = await CreateIssue(controller, { data });

  if (err) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.CREATE]: BLC_STATUS.ERROR,
      },
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: err,
      },
    });

    return;
  }

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.CREATE]: BLC_STATUS.SUCCESS,
    },
    successMessage: {
      ...state.successMessage,
      [CARD_NAME]: "Issue Was Created",
    },
  });

  await HandleSearch(controller, { ...params, action: "SEARCH" });
};

const HandleEdit: Handler<Params<Actions["EDIT"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.EDIT]: BLC_STATUS.LOADING,
    },
  });

  const { issueId, editedData } = params;
  const activePage = params.activePage || 1;

  const data: CreateIssuePayloadType = {
    issue: {
      ...editedData,
      start_date: formatDateToYYYYMMDD(editedData.start_date),
      due_date: formatDateToYYYYMMDD(editedData.due_date),
      custom_fields: (editedData.custom_fields || []).map((field) => ({
        id: field.id,
        value: field.value,
      })),
    },
  };

  const [resp, err] = await EditIssue(controller, { pk: issueId, data });

  if (err) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.EDIT]: BLC_STATUS.ERROR,
      },
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: err,
      },
    });

    return;
  }

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.EDIT]: BLC_STATUS.SUCCESS,
    },
    successMessage: {
      ...state.successMessage,
      [CARD_NAME]: "Issue Was Edited",
    },
  });

  await HandleSearch(controller, { ...params, action: "SEARCH", activePage });
};

const HandleHistory: Handler<Params<Actions["HISTORY"]>> = async (controller, params) => {
  const state = controller.getState();

  const { issueId } = params;

  const defaultFilter = {
    include: "journals",
  };

  const [resp, err] = await GetIssueDetail(controller, { pk: issueId, filter: defaultFilter });

  if (err) {
    controller.setState({
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: err,
      },
    });

    return;
  }

  controller.setState({
    IssueListSequence: {
      ...state.IssueListSequence,
      historyList: resp.issue?.journals || [],
    },
  });
};

const HandleGetMembership: Handler<Params<Actions["MEMBERSHIP"]>> = async (controller, params) => {
  const state = controller.getState();

  const { projectId } = params;

  const [resp, err] = await GetMembership(controller, { projectId });

  if (err) {
    controller.setState({
      IssueListSequence: {
        ...state.IssueListSequence,
        membershipProjectList: [],
      },
    });

    return;
  }

  const users = (resp?.memberships || [])
    .filter((member: MembershipItemType) => member.user)
    .map((member: MembershipItemType) => member.user);

  const deDuplicatedUsers = Array.from(
    new Map(users.map((user: { id: number; name: string }) => [user.id, user])).values()
  ) as { id: number; name: string }[];

  controller.setState({
    IssueListSequence: {
      ...state.IssueListSequence,
      membershipProjectList: deDuplicatedUsers || [],
    },
  });
};

const HandleExportExcel: Handler<Params<Actions["EXPORT_EXCEL"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.EXPORT_EXCEL]: BLC_STATUS.LOADING,
    },
  });

  const { data } = params;

  const rows = data.map((item: any) => ({
    ID: item?.id,
    Tracker: item?.tracker?.name || "",
    Status: item?.status?.name || "",
    Priority: item?.priority?.name || "",
    Subject: item?.subject,
    "Assigned Division": item?.custom_fields?.find((f: any) => f.name === "Assigned Division")?.value || "",
    "Assign To": item?.assigned_to?.name || "",
    "Cause and Solutions": item?.custom_fields?.find((f: any) => f.name === "Causes and Solutions")?.value || "",
    Created: `${item?.author?.name}, ${formatISOtoBEDateTime(item?.created_on).dateTimeShort || ""}`,
    Updated: formatISOtoBEDateTime(item?.updated_on).dateTimeShort || "",
    Project: item?.project?.name || "",
  }));

  const worksheet = XLSXUtils.json_to_sheet(rows);
  const workbook = XLSXUtils.book_new();
  
  XLSXUtils.book_append_sheet(workbook, worksheet, "Issues");

  const arrayBuffer = XLSXWrite(workbook, {
    bookType: "xlsx",
    type: "array",
  });

  downloadXLSX(arrayBuffer, "issue_list");

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
        [BTN_ACTS.EXPORT_EXCEL]: BLC_STATUS.SUCCESS,
    },
  });
};

/*================================ APIs ==============================*/

const GetCommonList: Handler = async (controller, params) => {
  const [customFieldsList, statusList, trackerList, priorityList] = await Promise.all([
    GetCustomFields.get({
      apiToken: controller.apiToken,
      extra: {
        division: controller.data.division,
      },
    }),
    getIssuesStatusList({
      controller,
      params,
    }),
    getTrackerList({
      controller,
      params,
    }),
    getPriorityList({
      controller,
      params,
    }),
  ]);

  return {
    customFieldsList,
    statusList,
    trackerList,
    priorityList,
  };
};

const GetProjectList: Handler = async (controller, params) => {
  const { filter } = params;

  // const mergedParams = {}

  const [resp, err] = await getProjectList({
    controller,
    ...(filter ? { params: filter } : {}),
  });

  if (err) return [null, err];

  return [resp, null];
};

const GetIssueList: Handler = async (controller, params) => {
  const { filter } = params;

  const mergedFilter = formatFilter(filter || {});

  const [resp, err] = await getIssuesList({
    controller,
    params: mergedFilter,
  });

  if (err) return [null, err];

  return [resp, null];
};

const GetIssueDetail: Handler<{ pk: number; filter: any }> = async (controller, params) => {
  const { pk, filter } = params;

  const [resp, err] = await getIssuesDetail({
    controller,
    pk,
    ...(filter ? { params: filter } : {}),
  });

  if (err) return [null, err];

  return [resp, null];
};

const GetMembership: Handler = async (controller, params) => {
  const { projectId } = params;

  const [resp, err] = await getMembershipsList({
    controller,
    pk: projectId,
  });

  if (err) return [null, err];

  return [resp, null];
};

const GetProjectMemberships: Handler<{ projectList: ProjectListType[] }> = async (
  controller,
  params
) => {
  const { projectList } = params;

  const results = await Promise.all(
    projectList.map((project) =>
      GetMembership(controller, { projectId: project.id }).then(([data, err]: [any, any]) => ({
        id: project.id,
        data: data?.memberships ?? [],
        err,
      }))
    )
  );

  return results.reduce((acc, curr) => {
    if (!curr.err) {
      acc[curr.id] = curr.data;
    }

    return acc;
  }, {} as MembershipMapType);
};

const CreateIssue: Handler<{ data: CreateIssuePayloadType }> = async (controller, params) => {
  const { data } = params;

  const [resp, err] = await createIssue({
    controller,
    data,
  });

  if (err) return [null, err];

  return [resp, err];
};

const EditIssue: Handler<{ pk: number; data: CreateIssuePayloadType }> = async (
  controller,
  params
) => {
  const { pk, data } = params;

  const [resp, err] = await editIssue({
    controller,
    data,
    pk,
  });

  if (err) return [null, err];

  return [resp, err];
};

/*================================ Utils ==============================*/

const formatFilter = (filter: FilterType) => {
  const acc: Record<string, any> = {};
  const useAdvancedFilter =
    Array.isArray(filter.projects) && filter.projects.length > 0 && !filter.project_id;

  const fList: (keyof FilterType | "created_on")[] = [];

  const checkStatusId = isValidCheck(filter?.status_id);

  if (useAdvancedFilter) {
    fList.push("status_id");
    acc["op[status_id]"] = "=";
    acc["v[status_id][]"] = checkStatusId ? [filter.status_id] : [1, 2, 3];
  } else {
    acc.status_id = checkStatusId ? filter.status_id : "*";
  }

  if (filter?.subject && filter.subject.trim() !== "") {
    if (useAdvancedFilter) {
      fList.push("subject");
      acc["op[subject]"] = "~";
      acc["v[subject][]"] = [filter.subject];
    } else {
      acc.subject = `~${filter.subject}`;
    }
  }

  const start = filter?.start_date ? formatDateToYYYYMMDD(filter.start_date) : null;
  const end = filter?.end_date ? formatDateToYYYYMMDD(filter.end_date) : null;

  if (start || end) {
    if (useAdvancedFilter) {
      fList.push("created_on");
      if (start && end) {
        acc["op[created_on]"] = "><";
        acc["v[created_on][]"] = [start, end];
      } else if (start) {
        acc["op[created_on]"] = ">=";
        acc["v[created_on][]"] = [start];
      } else if (end) {
        acc["op[created_on]"] = "<=";
        acc["v[created_on][]"] = [end];
      }
    } else {
      if (start && end) {
        acc.created_on = `><${start}|${end}`;
      } else if (start) {
        acc.created_on = `>=${start}`;
      } else if (end) {
        acc.created_on = `<=${end}`;
      }
    }
  }

  if (useAdvancedFilter) {
    fList.push("project_id");
    acc["op[project_id]"] = "=";
    acc["v[project_id][]"] = filter.projects;
  } else {
    acc.project_id = filter.project_id;
  }

  const optionalKeys: (keyof FilterType)[] = ["author_id", "assigned_to_id"];

  for (const key of optionalKeys) {
    const value = filter?.[key];
    if (isValidCheck(value)) {
      if (useAdvancedFilter) {
        fList.push(key);
        acc[`op[${key}]`] = "=";
        acc[`v[${key}][]`] = [value];
      } else {
        acc[key] = value;
      }
    }
  }

  if (isValidCheck(filter?.cf_6) && filter.cf_6 !== 0) {
    if (useAdvancedFilter) {
      fList.push("cf_6");
      acc["op[cf_6]"] = "~";
      acc["v[cf_6][]"] = [filter.cf_6];
    } else {
      acc["cf_6"] = `~${filter.cf_6}`;
    }
  }

  if (useAdvancedFilter && fList.length > 0) {
    acc["f[]"] = fList;
  }

  if (filter.offset !== undefined) {
    acc.offset = filter.offset;
  }

  return acc;
};

const extractUniqueUsers = (membershipMap: MembershipMapType) => {
  const map = new Map<number, { id: number; name: string }>();

  Object.values(membershipMap)
    .flat()
    .forEach((membership) => {
      if (membership.user && !map.has(membership.user.id)) {
        map.set(membership.user.id, membership.user);
      }
    });

  return Array.from(map.values()).sort((a, b) => a.id - b.id);
};

const getSpecificCustomFieldList = (
  customFieldList: CustomFieldsItemType[],
  fieldName: string,
  type: "issue" | "project"
) => {
  return (
    customFieldList.find((field) => field.name === fieldName && field.customized_type === type)
      ?.possible_values || []
  );
};

const convertCustomFieldNameToId = (
  fields: Partial<{ name: string; value: any }>[],
  masterFields: CustomFieldsItemType[]
) => {
  return fields.map((field) => {
    const match = masterFields.find((f) => f.name === field.name && f.customized_type === "issue");

    return match ? { id: match.id, value: field.value } : field;
  });
};

const isValidCheck = (value: any) => {
  return value !== undefined && value !== null && value !== "" && value !== 0;
};

const formatDivisionHasUserOptions = (divisionHasUser: DivisionHasUserType[]) => {
  return divisionHasUser.map((item) => ({
    key: item.division.id,
    value: item.division.id,
    text: item.division.name,
  }));
};
